import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { PlusCircle, PackageSearch, MapPin, QrCode as QrIcon } from "lucide-react";

const PRODUCT_TYPES = [
  { v: "powder", label: "Powder" },
  { v: "cubes", label: "Cubes" },
  { v: "bulk", label: "Bulk" },
  { v: "syrup", label: "Syrup" },
  { v: "flavoured", label: "Flavoured" },
];

function StatusBadge({ status }) {
  const map = {
    verified: "bg-primary/10 text-primary border-primary/30",
    pending: "bg-secondary/10 text-secondary border-secondary/30",
    rejected: "bg-destructive/10 text-destructive border-destructive/30",
  };
  return <Badge variant="outline" className={`${map[status] || ""} capitalize`}>{status}</Badge>;
}

export default function FpoDashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    batch_code: "", product_type: "powder", quantity_kg: "",
    harvest_date: "", processing_date: "", source_location: user?.location || "", notes: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/batches/mine");
      setItems(data.items);
    } catch { toast.error("Could not load your batches"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, quantity_kg: parseFloat(form.quantity_kg) };
      await api.post("/batches", payload);
      toast.success("Batch registered");
      setOpen(false);
      setForm({ batch_code: "", product_type: "powder", quantity_kg: "", harvest_date: "", processing_date: "", source_location: user?.location || "", notes: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-secondary">FPO Dashboard</div>
          <h1 className="font-marketing text-4xl sm:text-5xl mt-2">Namaste, {user?.name?.split(" ")[0]}</h1>
          <p className="text-muted-foreground mt-1">Log a batch, upload your proofs, hand the buyer a QR they can trust.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 rounded-full" data-testid="new-batch-btn">
              <PlusCircle className="h-4 w-4 mr-2"/>Register a batch
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="new-batch-dialog">
            <DialogHeader><DialogTitle className="font-heading">Register a new batch</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-2">
              <div><Label>Batch code</Label><Input value={form.batch_code} onChange={e=>set("batch_code", e.target.value)} required data-testid="batch-code-input"/></div>
              <div>
                <Label>Product type</Label>
                <Select value={form.product_type} onValueChange={v=>set("product_type", v)}>
                  <SelectTrigger data-testid="product-type-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {PRODUCT_TYPES.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Quantity (kg)</Label><Input type="number" step="0.1" min="0" value={form.quantity_kg} onChange={e=>set("quantity_kg", e.target.value)} required data-testid="quantity-input"/></div>
              <div><Label>Source location</Label><Input value={form.source_location} onChange={e=>set("source_location", e.target.value)} required data-testid="source-location-input"/></div>
              <div><Label>Harvest date</Label><Input type="date" value={form.harvest_date} onChange={e=>set("harvest_date", e.target.value)} required data-testid="harvest-date-input"/></div>
              <div><Label>Processing date</Label><Input type="date" value={form.processing_date} onChange={e=>set("processing_date", e.target.value)} required data-testid="processing-date-input"/></div>
              <div className="sm:col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e=>set("notes", e.target.value)} placeholder="Traditional wood-fired processing, no chemicals…" data-testid="notes-input"/></div>
              <DialogFooter className="sm:col-span-2">
                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="submit-batch-btn">Register batch</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: "Total batches", v: items.length },
          { l: "Verified", v: items.filter(b=>b.status==="verified").length },
          { l: "Pending", v: items.filter(b=>b.status==="pending").length },
          { l: "Scans (all)", v: items.reduce((a,b)=>a+(b.scans_count||0),0) },
        ].map(s => (
          <div key={s.l} className="rounded-2xl bg-card border border-border p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{s.l}</div>
            <div className="text-3xl font-heading font-semibold mt-2">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <h2 className="font-heading text-xl font-semibold">Your batches</h2>
        {loading ? (
          <div className="text-muted-foreground mt-4">Loading…</div>
        ) : items.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-border p-10 text-center bg-card">
            <PackageSearch className="h-8 w-8 mx-auto text-muted-foreground"/>
            <div className="mt-3 font-heading text-lg">No batches yet</div>
            <p className="text-sm text-muted-foreground">Register your first batch to generate a QR code for buyers.</p>
          </div>
        ) : (
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-batches-grid">
            {items.map(b => (
              <Link key={b.id} to={`/fpo/batch/${b.id}`} className="rounded-2xl bg-card border border-border p-5 hover:border-primary transition-colors group" data-testid={`batch-card-${b.batch_code}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-secondary">{b.product_type}</div>
                    <div className="font-heading text-lg font-semibold mt-1">{b.batch_code}</div>
                  </div>
                  <StatusBadge status={b.status}/>
                </div>
                <div className="mt-3 text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5"/>{b.source_location}</div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <div><span className="text-muted-foreground">Qty:</span> <b>{b.quantity_kg} kg</b></div>
                  <div className="flex items-center gap-1 text-primary opacity-0 group-hover:opacity-100 transition"><QrIcon className="h-4 w-4"/>Manage</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
